import { Temporal } from 'temporal-polyfill';
import { makeOccurrenceKey } from './occurrenceKey';
import type {
  ClassSeries,
  IsoDate,
  NthWeekday,
  Occurrence,
  RecurrenceRule,
  WallClockTime,
  Weekday,
} from './types';

/**
 * Safety valve. A month view asks for ~31 days and a year view for ~366, so any
 * expansion running past this bound means the caller passed a nonsensical range
 * or a rule slipped past validation.
 */
const MAX_OCCURRENCES = 5000;

export interface ExpandRequest {
  readonly series: ClassSeries;
  /** IANA timezone of the series' location. */
  readonly timeZone: string;
  /** Inclusive first date of the window of interest. */
  readonly rangeStart: Temporal.PlainDate;
  /** Inclusive last date of the window of interest. */
  readonly rangeEnd: Temporal.PlainDate;
}

function parseDate(iso: IsoDate): Temporal.PlainDate {
  return Temporal.PlainDate.from(iso);
}

function parseTime(time: WallClockTime): Temporal.PlainTime {
  return Temporal.PlainTime.from(time);
}

function isBefore(a: Temporal.PlainDate, b: Temporal.PlainDate): boolean {
  return Temporal.PlainDate.compare(a, b) < 0;
}

function isAfter(a: Temporal.PlainDate, b: Temporal.PlainDate): boolean {
  return Temporal.PlainDate.compare(a, b) > 0;
}

function maxDate(a: Temporal.PlainDate, b: Temporal.PlainDate): Temporal.PlainDate {
  return isAfter(a, b) ? a : b;
}

function minDate(a: Temporal.PlainDate, b: Temporal.PlainDate): Temporal.PlainDate {
  return isBefore(a, b) ? a : b;
}

/** Monday of the ISO week containing `date`. */
function weekStart(date: Temporal.PlainDate): Temporal.PlainDate {
  return date.subtract({ days: date.dayOfWeek - 1 });
}

/**
 * The date of the nth given weekday in a month, or null when the month has no
 * such date (e.g. a 4th Monday requested as a 5th).
 */
export function nthWeekdayOfMonth(
  year: number,
  month: number,
  weekday: Weekday,
  nth: NthWeekday,
): Temporal.PlainDate | null {
  const first = Temporal.PlainDate.from({ year, month, day: 1 });

  if (nth === -1) {
    const last = first.with({ day: first.daysInMonth });
    const back = (last.dayOfWeek - weekday + 7) % 7;
    return last.subtract({ days: back });
  }

  const forward = (weekday - first.dayOfWeek + 7) % 7;
  const candidate = first.add({ days: forward + (nth - 1) * 7 });
  return candidate.month === month ? candidate : null;
}

/**
 * All dates the rule produces inside `[rangeStart, rangeEnd]`, ascending.
 *
 * Works entirely in local calendar dates; timezones only enter when a date is
 * combined with the series' wall-clock time in {@link expandOccurrences}. That
 * split is what makes DST handling fall out for free: "18:00 local" stays 18:00
 * local because the date and the time are only joined at the very end.
 */
export function expandDates(request: ExpandRequest): Temporal.PlainDate[] {
  const { series, rangeStart, rangeEnd } = request;

  if (!series.active || series.deletedAt !== null) {
    return [];
  }

  const seriesStart = parseDate(series.startDate);
  const seriesEnd = series.endDate === null ? null : parseDate(series.endDate);

  // Clamp the requested window to the series' own lifetime.
  const from = maxDate(seriesStart, rangeStart);
  const to = seriesEnd === null ? rangeEnd : minDate(seriesEnd, rangeEnd);
  if (isAfter(from, to)) {
    return [];
  }

  const rule = series.recurrence;
  switch (rule.freq) {
    case 'once':
      return isBefore(seriesStart, from) || isAfter(seriesStart, to) ? [] : [seriesStart];
    case 'daily':
      return expandDaily(seriesStart, from, to);
    case 'weekly':
      return expandWeekly(rule.byWeekday, 1, seriesStart, from, to);
    case 'biweekly':
      return expandWeekly(rule.byWeekday, 2, seriesStart, from, to);
    case 'monthly':
      return expandMonthly(rule, from, to);
    default: {
      const exhaustive: never = rule;
      throw new Error(`Unsupported recurrence rule: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function expandDaily(
  seriesStart: Temporal.PlainDate,
  from: Temporal.PlainDate,
  to: Temporal.PlainDate,
): Temporal.PlainDate[] {
  const dates: Temporal.PlainDate[] = [];
  let cursor = maxDate(seriesStart, from);
  while (!isAfter(cursor, to)) {
    dates.push(cursor);
    if (dates.length > MAX_OCCURRENCES) {
      throw new Error('Daily expansion exceeded the occurrence limit');
    }
    cursor = cursor.add({ days: 1 });
  }
  return dates;
}

function expandWeekly(
  byWeekday: readonly Weekday[],
  intervalWeeks: 1 | 2,
  seriesStart: Temporal.PlainDate,
  from: Temporal.PlainDate,
  to: Temporal.PlainDate,
): Temporal.PlainDate[] {
  if (byWeekday.length === 0) {
    return [];
  }
  const weekdays = [...new Set(byWeekday)].sort((a, b) => a - b);

  // Week parity is anchored on the series start week, so a biweekly series keeps
  // its rhythm regardless of which window we happen to be rendering.
  const anchor = weekStart(seriesStart);
  let cursor = anchor;

  const fromWeek = weekStart(from);
  if (isAfter(fromWeek, anchor)) {
    const weeksElapsed = Math.floor(anchor.until(fromWeek, { largestUnit: 'day' }).days / 7);
    const aligned = Math.floor(weeksElapsed / intervalWeeks) * intervalWeeks;
    cursor = anchor.add({ weeks: aligned });
  }

  const dates: Temporal.PlainDate[] = [];
  while (!isAfter(cursor, to)) {
    for (const weekday of weekdays) {
      const date = cursor.add({ days: weekday - 1 });
      if (isBefore(date, from) || isAfter(date, to) || isBefore(date, seriesStart)) {
        continue;
      }
      dates.push(date);
    }
    if (dates.length > MAX_OCCURRENCES) {
      throw new Error('Weekly expansion exceeded the occurrence limit');
    }
    cursor = cursor.add({ weeks: intervalWeeks });
  }
  return dates;
}

function expandMonthly(
  rule: Extract<RecurrenceRule, { freq: 'monthly' }>,
  from: Temporal.PlainDate,
  to: Temporal.PlainDate,
): Temporal.PlainDate[] {
  const dates: Temporal.PlainDate[] = [];
  let cursor = Temporal.PlainYearMonth.from({ year: from.year, month: from.month });
  const last = Temporal.PlainYearMonth.from({ year: to.year, month: to.month });

  while (Temporal.PlainYearMonth.compare(cursor, last) <= 0) {
    const candidate =
      rule.mode === 'byMonthDay'
        ? monthDayCandidate(cursor, rule.monthDay)
        : nthWeekdayOfMonth(cursor.year, cursor.month, rule.weekday, rule.nth);

    if (candidate !== null && !isBefore(candidate, from) && !isAfter(candidate, to)) {
      dates.push(candidate);
    }
    if (dates.length > MAX_OCCURRENCES) {
      throw new Error('Monthly expansion exceeded the occurrence limit');
    }
    cursor = cursor.add({ months: 1 });
  }
  return dates;
}

/**
 * Months shorter than the requested day are skipped rather than clamped, which
 * matches iCalendar BYMONTHDAY: "the 31st" simply does not occur in February.
 */
function monthDayCandidate(
  yearMonth: Temporal.PlainYearMonth,
  monthDay: number,
): Temporal.PlainDate | null {
  if (monthDay < 1 || monthDay > yearMonth.daysInMonth) {
    return null;
  }
  return yearMonth.toPlainDate({ day: monthDay });
}

/**
 * Full occurrences for a series inside a date window.
 *
 * Overrides are deliberately not consulted here; {@link applyOverrides} layers
 * them on top so that the pure rule expansion stays independently testable.
 */
export function expandOccurrences(request: ExpandRequest): Occurrence[] {
  const { series, timeZone } = request;
  const startTime = parseTime(series.startTime);

  return expandDates(request).map((date) => {
    // Joining date + wall-clock time inside the zone is what preserves "6pm
    // local" across DST transitions. `compatible` disambiguation pushes forward
    // out of a spring-forward gap and picks the first instant of a fall-back
    // repeat, matching what calendar apps do.
    const start = date.toZonedDateTime({
      timeZone,
      plainTime: startTime,
    });
    return {
      key: makeOccurrenceKey(series.id, date.toString()),
      seriesId: series.id,
      title: series.title,
      originalDate: date.toString(),
      date: date.toString(),
      locationId: series.locationId,
      timeZone,
      start,
      end: start.add({ minutes: series.durationMinutes }),
      durationMinutes: series.durationMinutes,
      status: 'scheduled',
      rescheduled: false,
    } satisfies Occurrence;
  });
}

/**
 * Every date the rule produced strictly BEFORE `today` — the class's taught
 * history. Expanded a year at a time so a decade-old daily class stays under
 * the single-expansion safety limit. Ignores `active`/`deletedAt` on purpose:
 * callers ask about history precisely when reviving or reinterpreting a
 * series, and an archived class's past was still taught.
 */
export function expandPastDates(
  series: ClassSeries,
  timeZone: string,
  today: Temporal.PlainDate,
): Temporal.PlainDate[] {
  const todayIso = today.toString();
  if (series.startDate >= todayIso) {
    return [];
  }
  const yesterday = today.subtract({ days: 1 });
  const end =
    series.endDate !== null && series.endDate < yesterday.toString()
      ? Temporal.PlainDate.from(series.endDate)
      : yesterday;
  const live: ClassSeries = { ...series, active: true, deletedAt: null };

  const dates: Temporal.PlainDate[] = [];
  let cursor = Temporal.PlainDate.from(series.startDate);
  while (Temporal.PlainDate.compare(cursor, end) <= 0) {
    const chunkEnd = cursor.add({ days: 364 });
    const windowEnd = Temporal.PlainDate.compare(chunkEnd, end) > 0 ? end : chunkEnd;
    dates.push(...expandDates({ series: live, timeZone, rangeStart: cursor, rangeEnd: windowEnd }));
    cursor = windowEnd.add({ days: 1 });
  }
  return dates;
}
